import { AllowedPermissions } from '@lucidlink/dto/validation';
import { isNil } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { withStats, withStatsSync } from '#common/stats';
import { InvalidArgumentError, InvalidPermissionsError } from '#common/types';
import { TelemetryName } from '#telemetry/types';
import { ReadOnlyFilePermissionsManager } from './readonly-file-permissions-manager';
import { Permission, } from './types/file-permissions-manager';
import { FilePermissionsStats } from './types/stats';
export const DEFAULT_LIST_ACCESS_PAGE_LIMIT = 400;
export const createFilePermissionsManager = (permissions, idService, authorizer, kvHelperWasm, readOnly, listAccessPageLimit = DEFAULT_LIST_ACCESS_PAGE_LIMIT) => {
    const permissionManager = new FilePermissionsManagerImpl(permissions, idService, authorizer, kvHelperWasm, listAccessPageLimit);
    if (!readOnly) {
        return permissionManager;
    }
    return new ReadOnlyFilePermissionsManager(permissionManager);
};
class FileAccessIteratorContext {
    permsModule;
    callStats;
    authorizer;
    pageSize;
    permissions;
    filter;
    constructor(permsModule, callStats, authorizer, pageSize, filter) {
        this.permsModule = permsModule;
        this.callStats = callStats;
        this.authorizer = authorizer;
        this.pageSize = pageSize;
        if (filter && filter.length > 0) {
            this.filter = new Set(filter);
        }
    }
    [Symbol.asyncIterator]() {
        return this;
    }
    async next() {
        return withStats(this.callStats, async () => {
            if (isNil(this.permissions)) {
                this.permissions = await this.fetchPermissions();
            }
            return this.permissions.next();
        });
    }
    async fetchPermissions() {
        const accessMap = new Map();
        await this.forEachPermissionFiltered((accessInfo) => {
            const principalAccess = {
                principalId: accessInfo.principalId,
                permissions: accessInfo.permissions,
            };
            const existing = accessMap.get(accessInfo.fsEntryId);
            if (existing) {
                existing.principalsAccess.push(principalAccess);
            }
            else {
                accessMap.set(accessInfo.fsEntryId, {
                    fsEntryId: accessInfo.fsEntryId,
                    fsEntryPath: accessInfo.fsEntryPath,
                    fsEntryType: accessInfo.fsEntryType,
                    principalsAccess: [principalAccess],
                });
            }
        });
        return accessMap.values();
    }
    async forEachPermissionFiltered(callback) {
        let cursor;
        const filterArray = this.filter?.size ? [...this.filter] : undefined;
        do {
            const result = await this.authorizer.executeWithAdminPrivileges(() => {
                if (filterArray) {
                    return this.permsModule.listPermissionsPaginatedFilteredByFileId(filterArray, this.pageSize, cursor);
                }
                return this.permsModule.listPermissionsPaginated(this.pageSize, cursor);
            });
            for (const accessInfo of result.items) {
                callback(accessInfo);
            }
            cursor = result.nextCursor;
        } while (cursor);
    }
}
class FilePermissionsManagerImpl {
    permissions;
    idService;
    authorizer;
    kvHelperWasm;
    listAccessPageLimit;
    log = Logging.getLogger('FilePermissions');
    permStats = new FilePermissionsStats(this.log);
    constructor(permissions, idService, authorizer, kvHelperWasm, listAccessPageLimit) {
        this.permissions = permissions;
        this.idService = idService;
        this.authorizer = authorizer;
        this.kvHelperWasm = kvHelperWasm;
        this.listAccessPageLimit = listAccessPageLimit;
    }
    listAccessPartialFrom({ principalId, fileId, limit, cursor, }) {
        return withStats(this.permStats.listAccessPartialFrom, () => {
            return this.permissions.listPermissionsPartialFrom(principalId, fileId, limit, cursor);
        }, { principalId, fileId, limit, cursor });
    }
    listAccessPaginated({ principalId, limit, cursor, } = {}) {
        return withStats(this.permStats.listAccessPaginated, () => {
            if (!isNil(limit) && limit < 0) {
                throw new InvalidArgumentError(`Limit must not be negative, got ${limit}`);
            }
            if (principalId) {
                return this.permissions.listPermissionsPaginatedFilteredByPrincipalId([principalId], limit, cursor);
            }
            return this.permissions.listPermissionsPaginated(limit, cursor);
        }, { principalId, limit, cursor });
    }
    async grantAccess(principalId, entryId, permissions) {
        const operation = TelemetryName.GRANT_ACCESS;
        const telemetryValue = this.kvHelperWasm.makeTelemetryGrantAccessTuple(principalId, entryId, permissions);
        return withStats(this.permStats.grantAccess, () => {
            return this.authorizer.executeWithAdminPrivileges(async () => {
                this.validatePrincipal(principalId);
                await this.validatePrincipalPermissions(principalId, permissions);
                return await this.permissions.grantAccess(principalId, entryId, permissions);
            }, { operation, telemetryValue });
        }, { principalId, entryId, permissions });
    }
    async revokeAccess(principalId, entryId) {
        const operation = TelemetryName.REVOKE_ACCESS;
        const telemetryValue = this.kvHelperWasm.makeTelemetryRevokeAccessTuple(principalId, entryId);
        return withStats(this.permStats.revokeAccess, () => {
            return this.authorizer.executeWithAdminPrivileges(async () => {
                this.validatePrincipal(principalId);
                return await this.permissions.revokeAccess(principalId, entryId);
            }, { operation, telemetryValue });
        }, { principalId, entryId });
    }
    listAccess(filter) {
        return withStatsSync(this.permStats.listAccess, () => {
            return new FileAccessIteratorContext(this.permissions, this.permStats.listAccessIterator, this.authorizer, this.listAccessPageLimit, filter);
        }, { filter });
    }
    async getPrincipalsAccess(principals) {
        return withStats(this.permStats.getPrincipalsAccess, async () => {
            const result = new Map();
            for (const principalId of principals) {
                result.set(principalId, []);
            }
            let cursor;
            do {
                const page = await this.permissions.listPermissionsPaginatedFilteredByPrincipalId(principals, this.listAccessPageLimit, cursor);
                for (const item of page.items) {
                    result.get(item.principalId).push({
                        fsEntryId: item.fsEntryId,
                        fsEntryPath: item.fsEntryPath,
                        fsEntryType: item.fsEntryType,
                        permissions: item.permissions,
                    });
                }
                cursor = page.nextCursor;
            } while (cursor);
            return result;
        });
    }
    validatePrincipal(principalId) {
        if (principalId === this.idService.getAdminGroupId()) {
            throw new InvalidArgumentError('The Admin group cannot be granted or revoked permissions');
        }
    }
    async validatePrincipalPermissions(principalId, permissions) {
        const allowedPermissions = await this.idService.getPrincipalAllowedPermissions(principalId);
        if (allowedPermissions === AllowedPermissions.NONE) {
            throw new InvalidPermissionsError(`This principal (${principalId}) cannot receive permissions!`);
        }
        if (allowedPermissions !== AllowedPermissions.READ_WRITE && permissions.includes(Permission.WRITE)) {
            throw new InvalidPermissionsError(`This principal (${principalId}) cannot have write access!`);
        }
    }
    stats() {
        return this.permStats;
    }
}
